import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { PictureAsPdf, Description, OpenInNew, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchDashboardInfoRequest,
  clearDashboardInfo,
} from "../../../redux/DashboardInfo/DashboardInfoSlice";
import FAQDialog from "./FAQ";

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "bn", label: "Bengali" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "ml", label: "Malayalam" },
  { code: "pa", label: "Punjabi" },
  { code: "ur", label: "Urdu" },
  { code: "ar", label: "Arabic" },
  { code: "zh-CN", label: "Chinese (Simplified)" },
  { code: "zh-TW", label: "Chinese (Traditional)" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "ru", label: "Russian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "it", label: "Italian" },
  { code: "tr", label: "Turkish" },
  { code: "nl", label: "Dutch" },
  { code: "pl", label: "Polish" },
  { code: "sv", label: "Swedish" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
  { code: "no", label: "Norwegian" },
  { code: "id", label: "Indonesian" },
  { code: "ms", label: "Malay" },
  { code: "th", label: "Thai" },
  { code: "vi", label: "Vietnamese" },
  { code: "ro", label: "Romanian" },
  { code: "hu", label: "Hungarian" },
  { code: "cs", label: "Czech" },
  { code: "sk", label: "Slovak" },
  { code: "uk", label: "Ukrainian" },
  { code: "el", label: "Greek" },
  { code: "he", label: "Hebrew" },
  { code: "fa", label: "Persian" },
  { code: "sw", label: "Swahili" },
  { code: "af", label: "Afrikaans" },
];

const PAGE_OPTIONS = [
  { label: "Dashboard", value: "DASHBOARD" },
  { label: "Metal Management", value: "METAL_MANAGEMENT" },
  { label: "Transaction & Order", value: "TRANSACTION_AND_ORDER" },
  { label: "App Management", value: "APP_MANAGEMENT" },
  { label: "Products Management", value: "PRODUCT_MANAGEMENT" },
  { label: "Plan Management", value: "PLAN_MANAGEMENT" },
  { label: "Gifts", value: "GIFT" },
  { label: "Customers", value: "CUSTOMER" },
  { label: "Admin", value: "ADMIN" },
  { label: "Store", value: "STORE" },
  { label: "Offers", value: "OFFER" },
  { label: "Jeweller", value: "JEWELLER" },
  { label: "Subscriptions", value: "SUBSCRIPTION" },
  { label: "Settings", value: "SETTING" },
  { label: "Tickets Management", value: "TICKET_MANAGEMENT" },
  { label: "Booking Bullion", value: "BOOKING_BULLION" },
  { label: "FAQs", value: "FAQ" }
];

const translateText = async (text, targetLang) => {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: Translation request failed`);
  const json = await res.json();
  if (!json || !Array.isArray(json[0])) throw new Error("Unexpected response format");
  return json[0].filter((chunk) => chunk && chunk[0]).map((chunk) => chunk[0]).join("");
};

const translateHtmlPreservingStructure = async (html, targetLang) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
 
  const textNodes = [];
  const walker = document.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_TEXT,
    { acceptNode: (node) => node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT }
  );
  while (walker.nextNode()) textNodes.push(walker.currentNode);
 
  await Promise.all(
    textNodes.map((node) =>
      translateText(node.textContent.trim(), targetLang)
        .then((result) => { node.textContent = result; })
        .catch(() => {}) 
    )
  );
 
  return doc.body.innerHTML;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDescription = (description) => {
  if (!description) return "";
  if (Array.isArray(description)) return description.map((d) => d.insert || "").join("");
  if (typeof description === "string" && description.startsWith('"')) return JSON.parse(description);
  return description;
};

const getFileIcon = (url) => {
  if (/\.pdf$/i.test(url)) return <PictureAsPdf sx={{ color: "#e53935" }} />;
  if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url))
    return <i className="ri-video-line" style={{ fontSize: 20, color: "#7b1fa2" }} />;
  return <Description sx={{ color: "#1565c0" }} />;
};

// ─── File Preview Dialog ──────────────────────────────────────────────────────

const FilePreviewDialog = ({ file, onClose }) => {
  const url = file?.path;
  const displayName = file?.filename || url?.split("/").pop();

  return(
  <Dialog open={!!url} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
      <Typography fontWeight={600} fontSize={14} noWrap>
        {displayName}
      </Typography>
      <IconButton onClick={onClose} size="small">
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 2, textAlign: "center", minHeight: 300 }}>
      {/\.(jpg|jpeg|png|gif|webp)$/i.test(url) ? (
        <img src={url} alt="preview" style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8, objectFit: "contain" }} />
      ) : /\.pdf$/i.test(url) ? (
        <iframe src={url} title="PDF Preview" width="100%" height="500px" style={{ border: "none", borderRadius: 8 }} />
      ) : /\.(mp4|webm|ogg|mov|avi)$/i.test(url) ? (
        <video controls autoPlay controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}>
          <source src={url} />
        </video>
      ) : (
        <Box sx={{ py: 6 }}>
          <Typography color="text.secondary" mb={2}>Preview not available for this file type.</Typography>
          <Button variant="outlined" onClick={() => window.open(url, "_blank")}>Open in new tab</Button>
        </Box>
      )}
    </DialogContent>
  </Dialog>
  );
};


const DashboardInfo = ({ dialogMode = false, pageName: propPageName } = {}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const translationCache = useRef({});

  const incomingPageName = dialogMode
    ? propPageName || ""
    : location.state?.pageName || "";

  const [previewFile, setPreviewFile] = useState(null);
  const [faqOpen, setFaqOpen] = useState(false);

  const [selectedLang, setSelectedLang] = useState("en");
  const [translatedText, setTranslatedText] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");

  const { data } = useSelector((state) => state.dashboardInfo);

  const pageLabel = PAGE_OPTIONS.find((p) => p.value === incomingPageName)?.label || incomingPageName;
  const descriptionHtml = parseDescription(data?.description);
  
  const files = Array.isArray(data?.file) ? data.file : [];

  const imageFiles = files.filter((f) => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.path));
  const videoFiles = files.filter((f) => /\.(mp4|webm|ogg|mov|avi)$/i.test(f.path));
  const otherFiles = files.filter(
    (f) => !/\.(jpg|jpeg|png|gif|webp)$/i.test(f.path) && !/\.(mp4|webm|ogg|mov|avi)$/i.test(f.path)
  );

  useEffect(() => {
    setTranslatedText("");
    setSelectedLang("en");
    setTranslateError("");
    translationCache.current = {};
  }, [data]);

  useEffect(() => {
    dispatch(clearDashboardInfo());
    if (incomingPageName) {
      dispatch(fetchDashboardInfoRequest({ pageName: incomingPageName }));
    }
    return () => {
      dispatch(clearDashboardInfo());
    };
  }, [incomingPageName]);

  const handleLanguageChange = async (langCode) => {
    setSelectedLang(langCode);
    setTranslateError("");

    if (langCode === "en") {
      setTranslatedText("");
      return;
    }

    if (translationCache.current[langCode]) {
    setTranslatedText(translationCache.current[langCode]);
    return;
    }

    setIsTranslating(true);
    try {
      const result = await translateHtmlPreservingStructure(descriptionHtml, langCode);
      translationCache.current[langCode] = result;
      setTranslatedText(result);
    } catch (err) {
      console.error("Translation failed:", err);
      setTranslateError("Translation failed. Please try again.");
      setSelectedLang("en");
      setTranslatedText("");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleResetTranslation = () => {
    setTranslatedText("");
    setSelectedLang("en");
    setTranslateError("");
  };

  const displayedDescription = translatedText || descriptionHtml;

  // ── Empty state ──
  return (
    <Box sx={{ width: "100%", minHeight: "100vh", overflow: "auto" }}>
      <Box sx={{ width: "100%", mt: dialogMode ? 0 : { xs: 10, md: 10 } }}>
        <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 2, borderRadius: "16px" }} elevation={1}>

          {/* ── Header ── */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)",
              borderRadius: "12px",
              p: 3,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="ri-information-line" style={{ fontSize: 24, color: "#fff" }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={20} color="#fff" fontFamily="Aeonik">
                {pageLabel}
              </Typography>
              <Typography fontSize={13} color="rgba(255,255,255,0.75)">
                Page Information & Resources
              </Typography>
              
                <Typography
                  fontSize={12}
                  onClick={() => setFaqOpen(true)}
                  sx={{
                    color: "rgba(255,255,255,0.90)",
                    textDecoration: "underline",
                    textUnderlineOffset: 3,
                    cursor: "pointer",
                    mt: 0.5,
                    display: "inline-block",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  Frequently Asked Questions ↗
                </Typography>
            </Box>
          </Box>

          {/* ── Description ── */}
          { data? (
            <>
          {descriptionHtml && (
            <Box sx={{ backgroundColor: "#f9f6ff", border: "1px solid #e8d5ff", borderRadius: "10px", p: 3, mb: 3 }}>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Description
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isTranslating && (
                        <CircularProgress size={16} sx={{ color: "#673ab7" }} />
                      )}

                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel sx={{ fontSize: 13 }}>🌐 Translate</InputLabel>
                        <Select
                          value={selectedLang}
                          label="🌐 Translate"
                          onChange={(e) => handleLanguageChange(e.target.value)}
                          disabled={isTranslating}
                          sx={{
                            fontSize: 13,
                            borderRadius: "8px",
                            "& .MuiSelect-select": { py: "8px" },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: { maxHeight: 260, borderRadius: "10px" },
                            },
                          }}
                        >
                          {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                            <MenuItem key={code} value={code} sx={{ fontSize: 13 }}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {translatedText && (
                        <Tooltip title="Reset to original">
                          <IconButton
                            size="small"
                            onClick={handleResetTranslation}
                            sx={{ color: "#673ab7", border: "1px solid #e8d5ff", borderRadius: "8px" }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
              <Divider sx={{ mb: 2, borderColor: "#e8d5ff" }} />

              {translatedText && (
                    <Typography fontSize={11} color="#9c27b0" mb={1.5} sx={{ fontStyle: "italic" }}>
                      Translated to: {SUPPORTED_LANGUAGES.find((l) => l.code === selectedLang)?.label}
                    </Typography>
                  )}

                  {translateError && (
                    <Typography fontSize={12} color="error" mb={1.5}>
                      {translateError}
                    </Typography>
                  )}

              <Box
                fontSize={15}
                color="#333"
                lineHeight={1.8}
                dangerouslySetInnerHTML={{ __html: displayedDescription }}
                sx={{
                  "& strong": { fontWeight: 700 },
                  "& em": { fontStyle: "italic" },
                  "& u": { textDecoration: "underline" },
                  "& s": { textDecoration: "line-through" },
                  "& p": { margin: 0 },
                }}
              />
            </Box>
          )}

          {/* ── Images ── */}
          {imageFiles.length > 0 && (
            <Box mb={3}>
              <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Images
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 2 }}>
                {imageFiles.map((file, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      "&:hover .overlay": { opacity: 1 },
                    }}
                  >
                    <img src={file.path} alt={file.filename ||`file-${i}`} style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(103,58,183,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <Tooltip title="Open">
                        <IconButton onClick={() => setPreviewFile(file)} sx={{ color: "#fff" }}>
                          <OpenInNew />
                        </IconButton>
                      </Tooltip>
                      <Typography fontSize={11} color="#fff" noWrap sx={{ maxWidth: "100%" }}>
                        {file.filename || file.path.split("/").pop()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Attachments ── */}
          {otherFiles.length > 0 && (
            <Box mb={3}>
              <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Attachments
              </Typography>
              <Stack spacing={1}>
                {otherFiles.map((file, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      "&:hover": { backgroundColor: "#f3e5f5", borderColor: "#ce93d8" },
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={() => setPreviewFile(file)}
                  >
                    {getFileIcon(file.path)}
                    <Typography fontSize={14} color="#333" flex={1} noWrap>
                      {file.filename || file.path.split("/").pop()}
                    </Typography>
                    <OpenInNew sx={{ fontSize: 16, color: "#aaa" }} />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* ── Videos ── */}
          {videoFiles.length > 0 && (
            <Box mb={3}>
              <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Videos
              </Typography>
              <Stack spacing={2}>
                {videoFiles.map((file, i) => (
                  <Box key={i} sx={{ borderRadius: "10px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
                    <Typography
                      fontSize={13}
                      fontWeight={500}
                      color="#333"
                        sx={{ px: 1.5, py: 1, backgroundColor: "#f3e5f5" }}
                      noWrap
                    >
                      {file.filename || file.path.split("/").pop()}
                    </Typography>
                    <video controls controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: "100%", maxHeight: 320, display: "block", backgroundColor: "#000" }}>
                      <source src={file.path} />
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          </>) : (
            <Box sx={{ textAlign: "center", py: 6, color: "#bbb" }}>
            <i className="ri-information-line" style={{ fontSize: 40, marginBottom: 8 }} />
            <Typography fontWeight={600} fontSize={16} color="#bbb">
              No information available for this page yet.
            </Typography>
          </Box>
          )}  
        </Paper>
      </Box>

      <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
      {faqOpen && (
        <FAQDialog
          open={faqOpen}
          onClose={() => setFaqOpen(false)}
          faqs={data?.faq || []}
          pageLabel={pageLabel}
        />
      )}
    </Box>
  );
};

export default DashboardInfo;